import BasicTabsWithSearch from 'components/tabs/basic-tabs-with-search/basic-tabs-with-search.component'
import { tripTabs } from '../trip.constants'
import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import TabPanel from 'components/tabs/tab-panel/tab-panel.component'
import TripSelectionList from './trip-selection-list/trip-selection-list.component'
import { TripResponse } from 'store/trips/trips.types'
import { Box, Stack, useTheme } from '@mui/material'
import { Page } from 'store/common.types'
import AddButton from 'components/add-button/add-button.component'
import PrevNextPagination from 'components/prev-next-pagination/prev-next-pagination.component'
import CreateTripDialog from '../create-trip/create-trip-dialog'
import { Tab } from '../../../types'

interface TripSelectionProps {
  trips: TripResponse[] | undefined
  tabsValue: number
  setTabsValue: (value: number) => void
  page: Page | undefined
  handlePagination: (_event: ChangeEvent<unknown>, page: number) => void
  tabs?: Tab[]
  onTripsChanged?: () => void
}

const TripSelection = ({
  trips,
  tabsValue,
  setTabsValue,
  page,
  handlePagination,
  tabs = tripTabs,
  onTripsChanged,
}: TripSelectionProps) => {
  const theme = useTheme()

  const [search, setSearch] = useState('')
  const [createTripOpen, setCreateTripOpen] = useState(false)
  const tripCreateCompletion = useSelector(
    (state: RootState) => state.trips.tripCreateCompletion
  )

  useEffect(() => {
    if (tripCreateCompletion.isComplete) {
      setCreateTripOpen(false)
    }
  }, [tripCreateCompletion.isComplete])

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setTabsValue(newValue)
    setSearch('')
  }

  const filteredTrips = useMemo(() => {
    if (!trips) return trips

    const query = search.trim().toLowerCase()
    if (!query) return trips

    return trips.filter((trip) =>
      (trip?.firstDriverName || '').toLowerCase().includes(query) ||
      String(trip?.userTripId || '').toLowerCase().includes(query)
    )
  }, [trips, search])

  return (
    <Stack
      justifyContent="space-between"
      sx={{
        height: '100%',
        minHeight: 0,
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <BasicTabsWithSearch
          tabs={tabs}
          value={tabsValue}
          handleChange={handleChange}
          searchValue={search}
          searchPlaceholder="Search trips..."
          onSearchChange={(event) => setSearch(event.target.value)}
        >
          <TabPanel value={tabsValue} index={0}>
            <Stack spacing={1}>
              <TripSelectionList
                trips={filteredTrips}
                variant="driver"
                onTripsChanged={onTripsChanged}
              />
            </Stack>
          </TabPanel>
          <TabPanel value={tabsValue} index={1}>
            <Stack spacing={1}>
              <TripSelectionList
                trips={filteredTrips}
                variant="driver"
                onTripsChanged={onTripsChanged}
              />
            </Stack>
          </TabPanel>
        </BasicTabsWithSearch>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: theme.palette.common.white,
          borderTop: `1px solid ${theme.palette.divider}`,
          mt: 'auto',
          flexShrink: 0,
          pt: 0.5,
          pb: 1,
          px: 0.5,
          minHeight: 60,
        }}
      >
        <AddButton
          label="New Trip"
          handleClick={() => setCreateTripOpen(true)}
        />
        {!!page && page.totalPages > 0 && (
          <PrevNextPagination page={page} onChange={handlePagination} />
        )}
      </Box>
      <CreateTripDialog
        open={createTripOpen}
        onClose={() => setCreateTripOpen(false)}
      />
    </Stack>
  )
}

export default TripSelection
